import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const ContactSection = () => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();

  const handleEmailClick = () => {
    setValidationError(null);

    const body = `Hola equipo de IngemetalSur,

Me interesa contactarme con ustedes para solicitar informacion o una cotizacion.

---
Enviado desde: https://www.ingemetalsur.cl`;

    const mailto = `mailto:contacto@ingemetalsur.cl?cc=ingemetalsur@gmail.com&subject=${encodeURIComponent('Nuevo contacto desde IngemetalSur.cl')}&body=${encodeURIComponent(body)}`;

    window.open(mailto, '_blank');
  };

  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left: Info */}
          <div
            ref={leftRef}
            className={`reveal-left ${leftVisible ? 'reveal-visible' : ''}`}
          >
            <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Contacto</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2 mb-4 leading-tight">
              ¿Tienes un proyecto?<br />
              <span className="text-[#C88A2A]">Hablemos hoy</span>
            </h2>
            <p className="text-[#475569] text-base leading-relaxed mb-8">
              Solicita tu cotizacion sin compromiso. Nuestro equipo de constructora en Temuco te respondera en menos de 24 horas.
            </p>

            <div className="space-y-5 mb-8">
              {[
                { icon: 'ri-map-pin-line', label: 'Direccion', value: 'Parcela 18, Camino Vecinal, Sector El Retiro, Temuco' },
                { icon: 'ri-phone-line', label: 'Telefono', value: '+56 9 9226 5118', href: 'tel:+56992265118' },
                { icon: 'ri-mail-line', label: 'Correo', value: 'contacto@ingemetalsur.cl', href: 'mailto:contacto@ingemetalsur.cl' },
                { icon: 'ri-global-line', label: 'Sitio web', value: 'www.ingemetalsur.cl', href: 'https://www.ingemetalsur.cl' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A5C]/10 rounded-md shrink-0">
                    <i className={`${item.icon} text-[#C88A2A] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#64748B] font-medium uppercase tracking-wide">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-gray-800 text-sm font-medium hover:text-[#C88A2A] transition-colors">{item.value}</a>
                    ) : (
                      <div className="text-gray-800 text-sm font-medium">{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <a
                href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white font-bold px-6 py-3.5 rounded-md whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Escribir por WhatsApp
              </a>
              <button
                onClick={handleEmailClick}
                className="inline-flex items-center gap-3 bg-[#1A3A5C] hover:bg-[#152e45] text-white font-bold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
              >
                <i className="ri-mail-send-line text-xl"></i>
                Escribir por Email
              </button>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-lg overflow-hidden h-52">
              <iframe
                title="Ubicacion IngemetalSur Temuco"
                src="https://www.google.com/maps?q=-38.701185,-72.534385&output=embed&z=16"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: CTA Panel */}
          <div
            ref={rightRef}
            className={`bg-[#F8FAFC] rounded-xl p-8 flex flex-col justify-center reveal-right ${rightVisible ? 'reveal-visible' : ''}`}
          >
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#C88A2A]/10 rounded-full mx-auto mb-5">
                <i className="ri-mail-send-line text-[#C88A2A] text-3xl"></i>
              </div>
              <h3 className="text-[#0C1D2E] font-bold text-xl mb-3">Contactanos directamente</h3>
              <p className="text-[#475569] text-sm mb-6">
                Elige la forma de contacto que prefieras. Te responderemos en menos de 24 horas habiles.
              </p>

              <div className="space-y-3 max-w-sm mx-auto">
                <a
                  href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white font-bold px-6 py-3.5 rounded-md whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  WhatsApp rapido
                </a>
                <button
                  onClick={handleEmailClick}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#1A3A5C] hover:bg-[#152e45] text-white font-bold px-6 py-3.5 rounded-md whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <i className="ri-mail-line text-xl"></i>
                  Enviar correo
                </button>
                <a
                  href="tel:+56992265118"
                  className="w-full inline-flex items-center justify-center gap-3 bg-white border border-gray-200 hover:border-[#C88A2A] hover:text-[#C88A2A] text-[#0C1D2E] font-bold px-6 py-3.5 rounded-md whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <i className="ri-phone-line text-xl"></i>
                  Llamar ahora
                </a>
              </div>

              <p className="text-[#64748B] text-xs mt-6">
                Horario de atencion: Lunes a Jueves 08:00 – 18:00 / Viernes 08:00 – 14:00
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;